import type { ReactNode } from "react";
import type { SurveyOverview as Overview } from "../api/types.js";
import { Async } from "../components/Async.jsx";
import { SurveyExports } from "../components/SurveyExports.jsx";
import { usePortal } from "../app/context.js";
import { useAsync } from "../app/use-async.js";
import { href } from "../app/router.js";
import { utcMoment } from "../lib/format.js";

/**
 * Where a survey opens.
 *
 * `#/surveys/<id>` used to be the dense working screen — the plan, the walls,
 * the readiness checks, the exports, all at once. That is the right screen for
 * an hour of design work and the wrong one to ARRIVE at: somebody opening a
 * survey has two questions, what is this and what does it still need, and the
 * old screen answered both somewhere in the middle of answering forty others.
 *
 * ONE PRIMARY ACTION, AND IT IS THE NEXT THING. Not a menu of eight equal
 * cards: the screen works out what is outstanding and offers that, so the
 * answer to "what do I do now" is the biggest thing on the page. When nothing
 * is outstanding, the action becomes handing it over, which is the last thing
 * a surveyor does with a survey.
 *
 * HANDING OVER IS REFUSED BEFORE IT IS OFFERED. `buildSurveyIngestExport`
 * refuses a survey with no address, and the old flow let somebody press the
 * button and read the refusal. Here the button says what is missing and stays
 * quiet until it is not.
 */

export function SurveyOverview({ id }: { readonly id: string }): ReactNode {
  const { client } = usePortal();
  const { state, reload } = useAsync<Overview>(() => client.surveyOverview(id), [client, id]);

  return (
    <Async state={state} what="this survey" reload={reload}>
      {(survey) => <Body survey={survey} />}
    </Async>
  );
}

/** The one thing to do next, worked out from what the survey is missing. */
interface Next {
  readonly kind: "answers" | "address" | "handover" | "done";
  readonly title: string;
  readonly body: string;
  readonly action: string | null;
  readonly to: string | null;
}

function nextThing(survey: Overview): Next {
  if (survey.handedOverAt !== null) {
    return {
      kind: "done",
      title: "This survey has been handed over",
      body: `Sent ${utcMoment(survey.handedOverAt)}. Nothing else needs doing with it.`,
      action: null,
      to: null,
    };
  }
  if (survey.counts.rooms === 0) {
    return {
      kind: "answers",
      title: "Nothing has been walked yet",
      body:
        "No rooms have arrived from the phone for this survey. Scan the property, and they will appear here on their own.",
      action: null,
      to: null,
    };
  }
  if (survey.counts.answersLeft > 0) {
    const n = survey.counts.answersLeft;
    return {
      kind: "answers",
      title: n === 1 ? "One thing needs an answer" : `${n} things need an answer`,
      body:
        "We guessed them from the age of the house. Go through the rooms and say yes, or change them.",
      action: "Go to the rooms",
      to: href.surveyRooms(survey.surveyId),
    };
  }
  if (!survey.hasAddress) {
    return {
      kind: "address",
      title: "This survey needs an address",
      body:
        "Everything else is answered. The designer's portal files a property by its address, so it cannot be handed over without one.",
      action: "Add the address",
      to: href.job(survey.jobId),
    };
  }
  return {
    kind: "handover",
    title: "Ready to hand over",
    body:
      "Every question is answered and the address is in. Send it to the designer when you are ready.",
    action: "Open the hand-over",
    to: href.surveyPlan(survey.surveyId),
  };
}

function Body({ survey }: { readonly survey: Overview }): ReactNode {
  const next = nextThing(survey);
  const address = [survey.addressLine1, survey.postcode].filter(Boolean).join(", ");

  return (
    <>
      <div className="page-header">
        <p className="small">
          <a href={href.surveys()}>All surveys</a>
        </p>
        <h1>{survey.reference}</h1>
        <p className="lede">
          {address === "" ? "No address recorded" : address}
          {survey.surveyorName ? ` · walked by ${survey.surveyorName}` : ""}
          {survey.surveyedOn ? ` on ${survey.surveyedOn}` : ""}
        </p>
      </div>

      {/* The one primary action. Everything below it is quieter. */}
      <section
        className={`next-thing next-thing--${next.kind}`}
        data-testid="next-thing"
        aria-labelledby="next-thing-title"
      >
        <p className="next-thing__eyebrow">
          {next.kind === "done" ? "Finished" : "Next thing to do"}
        </p>
        <h2 id="next-thing-title">{next.title}</h2>
        <p className="next-thing__body">{next.body}</p>
        {next.action !== null && next.to !== null ? (
          <a className="primary next-thing__action" href={next.to}>
            {next.action}
          </a>
        ) : null}
      </section>

      <h2 className="section-head">What the survey found</h2>
      <div className="survey-facts" data-testid="survey-facts">
        <Fact label="Floors" value={String(survey.counts.floors)} />
        <Fact label="Rooms" value={String(survey.counts.rooms)} />
        <Fact
          label="Heat it needs"
          value={survey.totalHeatLossW === null ? "—" : `${kilowatts(survey.totalHeatLossW)} kW`}
          note={
            survey.noHeatLossReason ??
            (survey.totalHeatLossW === null ? null : kettles(survey.totalHeatLossW))
          }
        />
        <Fact label="Photos" value={String(survey.counts.photos)} />
      </div>

      <h2 className="section-head">The survey</h2>
      <div className="survey-doors">
        <Door
          to={href.surveyRooms(survey.surveyId)}
          title="Rooms and floors"
          note={`${survey.counts.rooms} ${survey.counts.rooms === 1 ? "room" : "rooms"} over ${
            survey.counts.floors === 1 ? "1 floor" : `${survey.counts.floors} floors`
          }`}
        />
        <Door
          to={href.surveyPlan(survey.surveyId)}
          title="The drawing"
          note="The plan, the walls, and the exports"
        />
        <Door
          to={href.jobPhotos(survey.jobId)}
          title="Photos"
          note={`${survey.counts.photos}, with where each was taken`}
        />
        <Door
          to={href.surveyChecks(survey.surveyId)}
          title="Checks and conditions"
          note="What it rests on, and what the MCS check says"
        />
        <Door
          to={href.job(survey.jobId)}
          title="The house"
          note="Address, who lives there, paperwork"
        />
      </div>

      {/* Every file this survey can become, gathered. They used to be in six
          places and no screen answered "what can I send somebody". */}
      <SurveyExports
        surveyId={survey.surveyId}
        jobId={survey.jobId}
        hasAddress={survey.hasAddress}
      />
    </>
  );
}

function Fact({
  label,
  value,
  note,
}: {
  readonly label: string;
  readonly value: string;
  readonly note?: string | null;
}): ReactNode {
  return (
    <div className="survey-fact">
      <p className="survey-fact__label">{label}</p>
      <p className="survey-fact__value">{value}</p>
      {note ? <p className="survey-fact__note">{note}</p> : null}
    </div>
  );
}

function Door({
  to,
  title,
  note,
}: {
  readonly to: string;
  readonly title: string;
  readonly note: string;
}): ReactNode {
  return (
    <a className="survey-door" href={to}>
      <span className="survey-door__title">{title}</span>
      <span className="survey-door__note">{note}</span>
    </a>
  );
}

/** One decimal, which is as precise as a heat pump is chosen. */
function kilowatts(watts: number): string {
  return (Math.round(watts / 100) / 10).toFixed(1);
}

/**
 * The figure in something anybody can picture.
 *
 * A householder asked "6.4 kW?" and a surveyor had nothing to say. A kettle is
 * about 3 kW, so the comparison is honest to the nearest whole kettle and says
 * "about" rather than pretending otherwise.
 */
function kettles(watts: number): string | null {
  const n = Math.round(watts / 3000);
  if (n < 1) return null;
  return n === 1 ? "about one kettle" : `about ${n} kettles`;
}
